import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { calculateTargetPoints } from './targetPoints';
import {
  getActiveDirectoryUsers,
  useUsersDirectory,
} from '../../lib/usersDirectory';

const CARRIERS = [
  'F&G', 'Ethos', 'Ameritas', 'Corebridge', 'Foresters',
  'Mutual of Omaha', 'Quility', 'Banner', 'North American',
  'American National', 'Allianz', 'American Equity', 'Other',
];

const STATUSES = [
  { value: 'application_started', label: 'Application Started' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_requirements', label: 'Pending Requirements' },
  { value: 'underwriting', label: 'Underwriting' },
  { value: 'approved', label: 'Approved' },
  { value: 'advanced_paid', label: 'Advanced Paid' },
  { value: 'declined', label: 'Declined' },
  { value: 'cancelled', label: 'Cancelled' },
];

const FREQUENCIES = ['monthly', 'quarterly', 'semi-annual', 'annual'];

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputClass = `px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm 
  text-white placeholder-gray-600 focus:outline-none focus:border-[#00AAFF] 
  transition-colors w-full`;

const selectClass = `px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm 
  text-gray-200 focus:outline-none focus:border-[#00AAFF] transition-colors w-full`;

export default function ApplicationForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [saving, setSaving] = useState(false);
  const [targetPointsOverridden, setTargetPointsOverridden] = useState(false);
  const { users: directoryUsers } = useUsersDirectory();

  const isAdmin = ['admin', 'super_admin', 'manager'].includes(user?.role);
  const agents = useMemo(
    () => (isAdmin ? getActiveDirectoryUsers(directoryUsers) : []),
    [isAdmin, directoryUsers],
  );

  const [form, setForm] = useState({
    // Client
    client_name: '',
    client_phone: '',
    client_email: '',
    // Policy
    carrier: '',
    product: '',
    policy_type: '',
    application_number: '',
    policy_number: '',
    premium_frequency: '',
    // Financials
    face_amount: '',
    premium: '',
    target_points: '',
    // Agent
    agent_id: isAdmin ? '' : user?.id,
    agent_name: isAdmin ? '' : user?.full_name,
    is_split: false,
    split_agent_id: '',
    split_agent_name: '',
    split_percent: '',
    // Status
    status: 'application_started',
    submitted_at: '',
    // Notes
    notes: '',
  });

  // Auto-calc target points until the user manually edits the field
  useEffect(() => {
    if (targetPointsOverridden) return;
    const computed = calculateTargetPoints({
      product: form.product,
      policy_type: form.policy_type,
      premium: form.premium,
      premium_frequency: form.premium_frequency,
    });
    const next = computed == null ? '' : String(computed);
    setForm((f) => (f.target_points === next ? f : { ...f, target_points: next }));
  }, [
    form.product,
    form.policy_type,
    form.premium,
    form.premium_frequency,
    targetPointsOverridden,
  ]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const setTargetPoints = (value) => {
    setTargetPointsOverridden(true);
    set('target_points', value);
  };

  const handleAgentSelect = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    set('agent_id', agentId);
    set('agent_name', agent?.full_name || '');
  };

  const handleSplitAgentSelect = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    set('split_agent_id', agentId);
    set('split_agent_name', agent?.full_name || '');
  };

  const validate = () => {
    if (!form.client_name.trim()) {
      addToast({ type: 'error', message: 'Client name is required' });
      return false;
    }
    if (!form.carrier) {
      addToast({ type: 'error', message: 'Carrier is required' });
      return false;
    }
    if (form.is_split && !form.split_percent) {
      addToast({ type: 'error', message: 'Split percent is required for split applications' });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const payload = {
        agency_id: user.agency_id,
        created_by: user.id,
        client_name: form.client_name.trim(),
        client_phone: form.client_phone || null,
        client_email: form.client_email || null,
        carrier: form.carrier,
        product: form.product || null,
        policy_type: form.policy_type || null,
        application_number: form.application_number || null,
        policy_number: form.policy_number || null,
        premium_frequency: form.premium_frequency || null,
        face_amount: form.face_amount ? parseFloat(form.face_amount) : null,
        premium: form.premium ? parseFloat(form.premium) : null,
        target_points: form.target_points
          ? parseFloat(form.target_points) : null,
        agent_id: form.agent_id || user.id,
        agent_name: form.agent_name || user.full_name,
        split_agent_id: form.is_split && form.split_agent_id ? form.split_agent_id : null,
        split_agent_name: form.is_split ? form.split_agent_name || null : null,
        is_split: form.is_split,
        split_percent: form.is_split && form.split_percent
          ? parseFloat(form.split_percent) : 0,
        status: form.status,
        submitted_at: form.status === 'submitted' && form.submitted_at
          ? new Date(form.submitted_at).toISOString()
          : form.status !== 'application_started' && !form.submitted_at
          ? new Date().toISOString()
          : form.submitted_at
          ? new Date(form.submitted_at).toISOString()
          : null,
        notes: form.notes || null,
      };

      const created = await api.applications.create(payload);
      addToast({ type: 'success', message: 'Application created' });
      navigate(`/applications/${created.id}`);
    } catch (err) {
      addToast({ type: 'error', message: 'Failed to create application' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/applications')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">New Application</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Create a new insurance application
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00AAFF] hover:bg-blue-500 
                     disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm 
                     font-medium rounded-lg transition-colors"
        >
          <FiSave className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Application'}
        </button>
      </div>

      {/* Section: Client */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
          Client Information
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Client Name" required>
            <input
              className={inputClass}
              placeholder="Full name"
              value={form.client_name}
              onChange={e => set('client_name', e.target.value)}
            />
          </Field>
          <Field label="Phone">
            <input
              className={inputClass}
              placeholder="(555) 000-0000"
              value={form.client_phone}
              onChange={e => set('client_phone', e.target.value)}
            />
          </Field>
          <Field label="Email">
            <input
              className={inputClass}
              type="email"
              placeholder="client@email.com"
              value={form.client_email}
              onChange={e => set('client_email', e.target.value)}
            />
          </Field>
        </div>
      </div>

      {/* Section: Policy */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
          Policy Details
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Carrier" required>
            <select
              className={selectClass}
              value={form.carrier}
              onChange={e => set('carrier', e.target.value)}
            >
              <option value="">Select carrier</option>
              {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Product">
            <input
              className={inputClass}
              placeholder="Product name"
              value={form.product}
              onChange={e => set('product', e.target.value)}
            />
          </Field>
          <Field label="Policy Type">
            <input
              className={inputClass}
              placeholder="e.g. Term, Whole Life, IUL"
              value={form.policy_type}
              onChange={e => set('policy_type', e.target.value)}
            />
          </Field>
          <Field label="Application #">
            <input
              className={inputClass}
              placeholder="App number"
              value={form.application_number}
              onChange={e => set('application_number', e.target.value)}
            />
          </Field>
          <Field label="Policy #">
            <input
              className={inputClass}
              placeholder="Policy number"
              value={form.policy_number}
              onChange={e => set('policy_number', e.target.value)}
            />
          </Field>
          <Field label="Premium Frequency">
            <select
              className={selectClass}
              value={form.premium_frequency}
              onChange={e => set('premium_frequency', e.target.value)}
            >
              <option value="">Select frequency</option>
              {FREQUENCIES.map(f => (
                <option key={f} value={f}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      {/* Section: Financials */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
          Financials
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Premium Amount">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 
                              text-sm">$</span>
              <input
                className={inputClass + ' pl-7'}
                type="number"
                placeholder="0.00"
                value={form.premium}
                onChange={e => set('premium', e.target.value)}
              />
            </div>
          </Field>
          <Field label="Face Amount (Coverage)">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 
                              text-sm">$</span>
              <input
                className={inputClass + ' pl-7'}
                type="number"
                placeholder="0.00"
                value={form.face_amount}
                onChange={e => set('face_amount', e.target.value)}
              />
            </div>
          </Field>
          <Field label="Target Points">
            <div className="relative">
              <input
                className={inputClass}
                type="number"
                placeholder="Auto from premium / product"
                value={form.target_points}
                onChange={e => setTargetPoints(e.target.value)}
              />
            </div>
          </Field>
        </div>
      </div>

      {/* Section: Agent */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
          Agent
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {isAdmin ? (
            <Field label="Primary Agent">
              <select
                className={selectClass}
                value={form.agent_id}
                onChange={e => handleAgentSelect(e.target.value)}
              >
                <option value="">Select agent</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id}>{a.full_name}</option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label="Primary Agent">
              <input
                className={inputClass + ' opacity-60 cursor-not-allowed'}
                value={user?.full_name || ''}
                disabled
              />
            </Field>
          )}

          <Field label="Split Application">
            <div className="flex items-center gap-3 h-[38px]">
              <button
                type="button"
                onClick={() => set('is_split', !form.is_split)}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  form.is_split ? 'bg-[#00AAFF]' : 'bg-gray-700'
                }`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white 
                                 transition-transform ${
                  form.is_split ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
              <span className="text-sm text-gray-400">
                {form.is_split ? 'Yes — split commission' : 'No split'}
              </span>
            </div>
          </Field>
        </div>

        {form.is_split && (
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-800">
            {isAdmin ? (
              <Field label="Split Agent">
                <select
                  className={selectClass}
                  value={form.split_agent_id}
                  onChange={e => handleSplitAgentSelect(e.target.value)}
                >
                  <option value="">Select split agent</option>
                  {agents
                    .filter(a => a.id !== form.agent_id)
                    .map(a => (
                      <option key={a.id} value={a.id}>{a.full_name}</option>
                    ))}
                </select>
              </Field>
            ) : (
              <Field label="Split Agent Name">
                <input
                  className={inputClass}
                  placeholder="Agent name"
                  value={form.split_agent_name}
                  onChange={e => set('split_agent_name', e.target.value)}
                />
              </Field>
            )}
            <Field label="Agent Split %">
              <input
                className={inputClass}
                type="number"
                min="1"
                max="99"
                placeholder="e.g. 50"
                value={form.split_percent}
                onChange={e => set('split_percent', e.target.value)}
              />
            </Field>
          </div>
        )}
      </div>

      {/* Section: Status */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
          Status
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Current Status">
            <select
              className={selectClass}
              value={form.status}
              onChange={e => set('status', e.target.value)}
            >
              {STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Date Submitted">
            <input
              className={inputClass}
              type="date"
              value={form.submitted_at}
              onChange={e => set('submitted_at', e.target.value)}
            />
          </Field>
        </div>
      </div>

      {/* Section: Notes */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
          Notes
        </h2>
        <textarea
          className={inputClass + ' resize-none'}
          rows={4}
          placeholder="Any additional notes about this application..."
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
        />
      </div>

      {/* Bottom Save */}
      <div className="flex justify-end pb-8">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-[#00AAFF] hover:bg-blue-500 
                     disabled:opacity-50 disabled:cursor-not-allowed text-white 
                     font-medium rounded-lg transition-colors"
        >
          <FiSave className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Application'}
        </button>
      </div>
    </div>
  );
}
